import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./DlqiDetails.css";

const getDifficultyLevel = (s) => {
  if (s >= 0 && s <= 1) return { text: "No Impact", color: "#888" };
  if (s >= 2 && s <= 5) return { text: "Small Impact", color: "#4098e3" };
  if (s >= 6 && s <= 10) return { text: "Moderate Impact", color: "#f3d53a" };
  if (s >= 11 && s <= 20) return { text: "Large Impact", color: "#e67d1b" };
  if (s >= 21 && s <= 30) return { text: "Extremely Large Impact", color: "#f44336" };
  return { text: "", color: "" };
};

const getConditionTitle = () => {
  const host = window.location.hostname;
  if (host === "lifestyle1.goqii.com") return "ECZEMA";
  return "ATOPIC DERMATITIS";
};

const getDlqiReason = (score, title) => {
  const condition = title === "ECZEMA" ? "Eczema" : "Atopic Dermatitis";
  if (score >= 0 && score <= 1) return `${condition} has no impact on your quality of life`;
  if (score >= 2 && score <= 5) return `${condition} has a small impact on your quality of life`;
  if (score >= 6 && score <= 10) return `${condition} has a moderate impact on your quality of life`;
  if (score >= 11 && score <= 20) return `${condition} has a large impact on your quality of life`;
  if (score >= 21 && score <= 30) return `${condition} has an extremely large impact on your quality of life`;
  return "";
};

const DlqiDetails = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const type = urlParams.get("type") || "";

  const title = getConditionTitle();

  const [score, setScore] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [logdate, setLogDate] = useState("");
  const [pfizerUser, setPfizerUser] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [nextDate, setNextDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchDlqiScore()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setScore(data.score);
          setReportUrl(data.reportUrl || "");
          setLogDate(data.logDate || "");
          setPfizerUser(data.pfizerUser || false);
        }
      })
      .catch((err) => console.error("fetchDlqiScore:", err))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    if (logdate) {
      const next = new Date(Date.parse(logdate) + 7 * 24 * 60 * 60 * 1000);
      setNextDate(next);
      if (next > new Date()) setShowPopup(true);
    }
  }, [logdate]);

  const formatDate = (d) => {
    if (!d) return "";
    const obj = d instanceof Date ? d : new Date(d);
    return `${obj.getDate()}-${obj.toLocaleString("default", { month: "short" })}-${obj.getFullYear().toString().slice(-2)}`;
  };

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const difficulty = score != null ? getDifficultyLevel(score) : { text: "", color: "" };
  const dlqiReason = score != null ? getDlqiReason(score, title) : "";

  const viewAllHref = pfizerUser
    ? `https://goqii.com/app?fsn=242&fssn=2&fai=${encodeURIComponent(JSON.stringify({ url: `https://lifestyle1.goqii.com/selfstatspfizer?uuid=${uuid}`, title: "Flareup" }))}`
    : `https://goqii.com/app?fsn=242&fssn=2&fai=${encodeURIComponent(JSON.stringify({ url: `https://lifestyle1.goqii.com/self-stats?uuid=${uuid}`, title: "Flareup" }))}`;

  return (
    <section className="dlqi-page">
      <div className="dlqi-container">
        {loading ? (
          <div className="dlqi-loading">{t("common.loading", "Loading...")}</div>
        ) : (
          <>
            <p className="dlqi-description">
              {!type
                ? "Dermatology Life Quality Index is a questionnaire to understand how your Psoriasis impacts your daily life in the past one week."
                : dlqiReason}
            </p>

            {!type && (
              <div className="dlqi-take-btn-wrap">
                {showPopup ? (
                  <div className="dlqi-completed-box">
                    You recently completed a DLQI on <strong>{formatDate(logdate)}</strong>.
                    The next one is on {formatDate(nextDate)}.
                  </div>
                ) : (
                  <button
                    type="button"
                    className="dlqi-take-btn"
                    onClick={() => { window.location.href = `/dlciquestionaireone?${buildQs()}`; }}
                  >
                    TAKE A DLQI
                  </button>
                )}
              </div>
            )}

            {score != null && (
              <>
                <label className="dlqi-label">{type ? "DLQI Score" : "Previous DLQI"}</label>
                <div className="dlqi-score-card">
                  <div style={{ flex: 1 }}>
                    <p className="dlqi-difficulty-text" style={{ color: difficulty.color || "var(--text-primary)" }}>
                      {difficulty.text}
                    </p>
                    <div className="dlqi-difficulty-bar" style={{ background: difficulty.color || "#ccc" }} />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <p className="dlqi-score-value">{score}</p>
                    <p className="dlqi-score-max">(Out of 30)</p>
                  </div>
                  {reportUrl ? (
                    <a
                      href={`https://goqii.com/app?fsn=244&fssn=0&fai=${encodeURIComponent(JSON.stringify({ url: reportUrl, title: "DLQI-Report" }))}`}
                      className="dlqi-report-link"
                    >
                      View Report
                    </a>
                  ) : null}
                </div>

                <div className="dlqi-view-all-wrap">
                  <a href={viewAllHref} className="dlqi-view-all-link">
                    View All Previous DLQI
                  </a>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default DlqiDetails;
